import { useEffect, useImperativeHandle, useRef, type Ref } from "react";

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      "error-callback": () => void;
      "expired-callback": () => void;
      theme: "auto" | "light" | "dark";
      size: "normal" | "flexible" | "compact";
    }
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

// One script for the page, however many times the component mounts.
const loadTurnstile = (): Promise<TurnstileApi> => {
  if (window.turnstile) return Promise.resolve(window.turnstile);

  return new Promise((resolve, reject) => {
    const fail = (): void => reject(new Error("Turnstile unavailable"));
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    const script = existing ?? document.createElement("script");

    script.addEventListener(
      "load",
      () => (window.turnstile ? resolve(window.turnstile) : fail()),
      { once: true }
    );
    script.addEventListener("error", fail, { once: true });

    if (!existing) {
      script.src = SCRIPT_SRC;
      document.head.appendChild(script);
    }
  });
};

export interface TurnstileHandle {
  reset: () => void;
}

interface TurnstileProps {
  siteKey: string;
  /** Fires with a token on success, and with null when it expires, errors or is reset. */
  onToken: (token: string | null) => void;
  onError: () => void;
  ref?: Ref<TurnstileHandle>;
}

export const Turnstile = ({ siteKey, onToken, onError, ref }: TurnstileProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  // Held in a ref so a parent re-render never tears the widget down and rebuilds it.
  const handlersRef = useRef({ onToken, onError });
  useEffect(() => {
    handlersRef.current = { onToken, onError };
  });

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (!widgetIdRef.current) return;
      window.turnstile?.reset(widgetIdRef.current);
      handlersRef.current.onToken(null);
    },
  }));

  useEffect(() => {
    let cancelled = false;

    loadTurnstile()
      .then(api => {
        if (cancelled || !containerRef.current) return;
        widgetIdRef.current = api.render(containerRef.current, {
          sitekey: siteKey,
          callback: token => handlersRef.current.onToken(token),
          "error-callback": () => {
            handlersRef.current.onToken(null);
            handlersRef.current.onError();
          },
          "expired-callback": () => handlersRef.current.onToken(null),
          theme: "light",
          size: "flexible",
        });
      })
      .catch(() => {
        if (!cancelled) handlersRef.current.onError();
      });

    // StrictMode mounts twice in dev, so the first widget has to go with it.
    return () => {
      cancelled = true;
      if (widgetIdRef.current) {
        window.turnstile?.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  return <div ref={containerRef} />;
};
